import {
    AppComboBoxOnExecuteParameter,
    AppComboBoxPreviewOnExecuteParameter,
    AppComboBoxOption,
} from '@1js/acui-combobox';
import type { RibbonControlProps } from '@1js/acui-ribbon-like/lib/UISurfaces/Ribbon/Controls/RibbonControlProps';
import { computedFn } from 'mobx-utils/lib/computedFn';
import { getStore } from 'owa-editor-ribbon-secondary-ui/lib/store/store';
import { lazyUpdateSecondClassState, RibbonViewState } from 'owa-editor-ribbonplugin-store';
import type EditorViewState from 'owa-editor/lib/store/schema/EditorViewState';
import { callEditorApi } from 'owa-editor/lib/utils/callEditorApi';
import { callFormatApi } from 'owa-editor/lib/utils/callFormatApi';
import { getComposeRibbonId } from 'owa-editor/lib/utils/getComposeRibbonId';
import { getFontSizeFromSizeOrIndex } from 'owa-format-options';
import loc from 'owa-localize';
import { FormatControlId } from 'owa-ribbon-ids';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import createWrapper from '../util/controlOnExecuteHandler';
import styles from './fontPicker.scss';
import { startLivePreview, stopLivePreview } from '../util/livePreviewHandler';
import { getKeytipString, constructKeytip } from 'owa-command-ribbon';
import classnamesBind from 'classnames/bind';
import { isFeatureEnabled } from 'owa-feature-flags';

const classNames = classnamesBind.bind(styles);
const DEFAULT_FONT_SIZE = 3;

function getDefaultFontSize() {
    const userOption = getUserConfiguration().UserOptions;
    const size = userOption?.ComposeFontSize || DEFAULT_FONT_SIZE;
    return getFontSizeFromSizeOrIndex(size).toString();
}

const getFontDropDownOptions = (): AppComboBoxOption[] => {
    const viewState = getStore().fontSize;
    const appComboBoxOptions: AppComboBoxOption[] = [];
    viewState.items.forEach(item => {
        appComboBoxOptions.push({
            type: 'AppComboBoxItem',
            key: item.selectValue,
            text: item.displayValue,
            ariaLabel: item.displayValue,
            styles: {
                optionTextWrapper: { width: 80 },
                rootHovered: styles.comboBoxOptionHovered,
            },
        });
    });
    return appComboBoxOptions;
};

export const getFontSizePickerControl = computedFn(
    (
        editorViewState: EditorViewState | undefined,
        ribbonViewState: RibbonViewState | undefined,
        tabId: number | undefined
    ): RibbonControlProps => {
        const fontDropDownOptions = getFontDropDownOptions();
        const defaultFontSize = getDefaultFontSize();
        return {
            type: 'AppComboBoxProps',
            id: getComposeRibbonId(
                FormatControlId.FontSizePicker.toString(),
                editorViewState
            ).toString(),
            allowFreeForm: true,
            label: loc('Font size'),
            width: 64,
            options: fontDropDownOptions,
            defaultSelectedKey: defaultFontSize,
            selectedKey: ribbonViewState?.formatViewState?.fontSize || defaultFontSize,
            onExecute: (parameter: AppComboBoxOnExecuteParameter) => {
                if (!!editorViewState && !!ribbonViewState) {
                    stopLivePreview(editorViewState);
                    const newFontSize = parameter.newValue['key'];
                    callFormatApi(editorViewState, 'setFontSize', [newFontSize + 'pt']);

                    lazyUpdateSecondClassState.importAndExecute(
                        ribbonViewState.formatViewState,
                        callEditorApi(editorViewState, 'getStyleBasedFormatState')
                    );
                }
            },
            onPreviewExecute: (parameter: AppComboBoxPreviewOnExecuteParameter) => {
                startLivePreview(editorViewState, () => {
                    const newFontSize = parameter.newValue['key'];
                    callFormatApi(editorViewState!, 'setFontSize', [newFontSize + 'pt']);
                });
            },
            onMenuOpen: createWrapper(editorViewState, ribbonViewState, undefined, false),
            pinToPreviousComboBox: true,
            styles: {
                optionsContainerWrapper: classNames(
                    styles.fontPickerContainer,
                    styles.fontSizePicker
                ),
            },
            onAfterMenuDismiss: () => {
                stopLivePreview(editorViewState);
            },
            keytipProps:
                tabId && isFeatureEnabled('mon-ribbon-keytips')
                    ? constructKeytip(
                          [getKeytipString(tabId)],
                          getKeytipString(FormatControlId.FontSizePicker)
                      )
                    : undefined,
        };
    }
);
